import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, MapPin, MessageCircle, Phone, Loader2 } from "lucide-react";
import { FadeUp, SectionLabel } from "./Reveal";
import { CONTACT_INFO, VENTURES } from "../../data/ventures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", interest: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-white/10 bg-slate-900/60 px-5 py-3.5 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-200 focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20";

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        phone: form.phone || null,
        interest: form.interest || null,
      });
      toast.success("Message sent! The Veerox team will reach out soon.");
      setForm(INITIAL);
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-36">
      <div className="pointer-events-none absolute left-1/2 top-20 h-96 w-[600px] -translate-x-1/2 rounded-full bg-blue-600/10 blur-[150px]" />
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1fr_1.1fr]">
          <div>
            <FadeUp>
              <SectionLabel>Contact</SectionLabel>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Let&apos;s build
                <br />
                <span className="text-gradient-cyan">something great.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                Partnership, product enquiry, or a bold idea — drop us a line
                and the Veerox team will get back to you.
              </p>
            </FadeUp>

            <FadeUp delay={0.3}>
              <div className="mt-10 space-y-5">
                <a
                  href={CONTACT_INFO.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="group flex items-center gap-4"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] transition-colors duration-300 group-hover:border-cyan-400/40">
                    <MessageCircle className="h-5 w-5 text-cyan-400" />
                  </span>
                  <div>
                    <div className="font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-500">
                      WhatsApp
                    </div>
                    <div className="text-sm font-medium text-white">{CONTACT_INFO.whatsapp}</div>
                  </div>
                </a>
                <a
                  href={CONTACT_INFO.phoneLink}
                  data-testid="contact-call-agent"
                  className="group flex items-center gap-4"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] transition-colors duration-300 group-hover:border-cyan-400/40">
                    <Phone className="h-5 w-5 text-cyan-400" />
                  </span>
                  <div>
                    <div className="font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-500">
                      Call Agent
                    </div>
                    <div className="text-sm font-medium text-white">{CONTACT_INFO.phone}</div>
                  </div>
                </a>
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                    <Mail className="h-5 w-5 text-cyan-400" />
                  </span>
                  <div>
                    <div className="font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-500">
                      Email
                    </div>
                    {CONTACT_INFO.emails.map((em) => (
                      <a
                        key={em}
                        href={`mailto:${em}`}
                        className="block text-sm font-medium text-white transition-colors duration-200 hover:text-cyan-300"
                      >
                        {em}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-4" data-testid="contact-location">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                    <MapPin className="h-5 w-5 text-cyan-400" />
                  </span>
                  <div>
                    <div className="font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-500">
                      Location
                    </div>
                    <div className="text-sm font-medium text-white">{CONTACT_INFO.location}</div>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="glass rounded-2xl p-8 md:p-10"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-400">
                    Full Name *
                  </label>
                  <input
                    data-testid="contact-input-name"
                    className={inputCls}
                    placeholder="Your name"
                    value={form.name}
                    onChange={set("name")}
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-400">
                    Email *
                  </label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className={inputCls}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={set("email")}
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-400">
                    Phone / WhatsApp
                  </label>
                  <input
                    data-testid="contact-input-phone"
                    className={inputCls}
                    placeholder="+91 …"
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-400">
                    Interested In
                  </label>
                  <select
                    data-testid="contact-input-interest"
                    className={`${inputCls} appearance-none`}
                    value={form.interest}
                    onChange={set("interest")}
                  >
                    <option value="" className="bg-slate-900">Select a venture…</option>
                    {VENTURES.map((v) => (
                      <option key={v.id} value={v.name} className="bg-slate-900">
                        {v.name}
                      </option>
                    ))}
                    <option value="Partnership" className="bg-slate-900">Partnership / Other</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="mb-2 block font-mono-tech text-[0.62rem] uppercase tracking-[0.24em] text-slate-400">
                  Message *
                </label>
                <textarea
                  data-testid="contact-input-message"
                  rows={5}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us about your project or enquiry…"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="btn-primary mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 font-display text-base font-semibold text-white disabled:opacity-60 sm:w-auto"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
